import React, {createContext, useContext, useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {jwtDecode} from "jwt-decode";
import {mergeStateObject} from "../utils/merger";
import {fetchWrapper} from "../helpers";
import {Button, message, notification, Space} from "antd";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

export const OrganizationContextProvider = createContext(null);

export const useOrganization = () => useContext(OrganizationContextProvider);

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const OrganizationContext = ({children}) => {
	const [notificationApi, notificationContextHolder] =
			notification.useNotification();
	const navigate = useNavigate();
	const {t} = useTranslation();
	const user = useSelector((obj) => obj.auth.user);
	const [organization, setOrganization] = useState(null);

	const handleSetOrganization = (organization) => {
		mergeStateObject(setOrganization, organization);
	};

	const getOrganizationDetails = async (id) => {
		if (!id) return {};
		const response = await fetchWrapper.get(`${baseUrl}/organizations/${id}`);
		return response;
	};

	const setOrganizationFile = async (file) => {
		const formData = new FormData();
		formData.append('file', file);
		const response = await fetchWrapper.post(`${baseUrl}/organizations/${organization?.id}/files`, formData);
		if (response.fileUrl){
			handleSetOrganization({fileUrl: response.fileUrl});
			message.success(t("functions.successfullyUploaded"));
		} else {
			message.error(t("functions.wrong"));
		}
	};

	const setOrganizationLogo = async (file) => {
		const formData = new FormData();
		formData.append('file', file);
		const response = await fetchWrapper.post(`${baseUrl}/organizations/${organization?.id}/logo`, formData);
		if (response?.logoUrl){
			handleSetOrganization({logoUrl: response.logoUrl});
			message.success(t("functions.successfullyUploaded"));
		} else {
			message.error(t("functions.wrong"));
		}
	};

	const openNotificationUnreadMessages = (count) => {
		const key = `open${Date.now()}`;
		const btn = (
				<Space>
					<Button
							type="primary"
							size="small"
							onClick={() => navigate("/moderation")}
					>
						{t("moderation.goToModeration")}
					</Button>
				</Space>
		);
		notificationApi.open({
			message: t("moderation.header"),
			description: t("moderation.unreadModerationCountMessage", {count: count}),
			btn,
			key,
		});
	};

	const checkModerationMessages = async () => {
		const response = await fetchWrapper.get(`${baseUrl}/post/moderation?page=1&limit=11&approvedExtended=user_approved`);
		const unreadMessages = await response?.posts?.length ?? 0;

		if (unreadMessages && !window.location.pathname.includes('moderation')) {
			openNotificationUnreadMessages(unreadMessages > 9 ? '9+' : unreadMessages);
		}
	};

	useEffect(() => {
		let orgFromToken = null;
		(async () => {
			if (user){
				if (jwtDecode(user?.accessToken)?.organizationId){
					orgFromToken = {
						id: jwtDecode(user?.accessToken)?.organizationId,
						curator: jwtDecode(user?.accessToken)?.curator
					}
				} else {
					orgFromToken = {
						id: user?.organizationId,
						curator: user?.curator
					}
				}
				const orgDetails = await getOrganizationDetails(orgFromToken?.id);
				handleSetOrganization({
					...orgFromToken,
					...orgDetails
				});
			}
		})();
	}, [user]);

	useEffect(() => {
		if (organization?.moderatorExist && user?.moderatorOrg) {
			checkModerationMessages();
		}
	}, [user, organization]);

	return (
			<OrganizationContextProvider.Provider value={{organization, setOrganizationFile, setOrganizationLogo}}>
				{notificationContextHolder}
				{children}
			</OrganizationContextProvider.Provider>
	);
};

export default OrganizationContext;